import { ReactNode, UIEvent, useState } from 'react';
import { cn } from '@localagent/ui/lib/utils';

type PageShellProps = {
  title: ReactNode;
  description?: ReactNode;
  headerActions?: ReactNode;
  /** Rendered inside the header band, below the title — tab rows and filters
   *  belong here so they do not scroll away with the content. */
  headerSlot?: ReactNode;
  children: ReactNode;
};

export function PageShell({ title, description, headerActions, headerSlot, children }: PageShellProps) {
  const [scrolled, setScrolled] = useState(false);

  function handleScroll(event: UIEvent<HTMLDivElement>) {
    setScrolled(event.currentTarget.scrollTop > 0);
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div
        data-testid="page-shell-header"
        data-scrolled={scrolled ? 'true' : 'false'}
        className={cn(
          'z-10 shrink-0 border-b bg-background px-6 py-4 transition-shadow',
          scrolled && 'shadow-md',
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">{title}</h1>
            {description ? (
              <p className="text-sm text-muted-foreground">{description}</p>
            ) : null}
          </div>
          {headerActions ? (
            <div className="flex shrink-0 items-center gap-2">{headerActions}</div>
          ) : null}
        </div>
        {headerSlot ? <div className="mt-4">{headerSlot}</div> : null}
      </div>

      <div
        data-testid="page-shell-scroll"
        onScroll={handleScroll}
        className="min-h-0 flex-1 overflow-y-auto p-6"
      >
        <div
          data-testid="page-shell-scrim"
          aria-hidden="true"
          className={cn(
            'pointer-events-none sticky top-0 -mb-3 h-3 bg-gradient-to-b from-background to-transparent transition-opacity',
            scrolled ? 'opacity-100' : 'opacity-0',
          )}
        />
        {children}
      </div>
    </div>
  );
}
